import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  CpuChipIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';
import SimpleFadeIn from '../components/SimpleFadeIn';
import { apiEndpoint } from '../config';
import ModernButton from '../components/ModernButton';
import TestimonialsSection from '../components/TestimonialsSection';
import CaseStudiesSection from '../components/CaseStudiesSection';
import UseCases from '../components/UseCases';
import FAQ from '../components/FAQ';
import TechnologyPartners from '../components/TechnologyPartners';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0.4;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.7) 0%,
    rgba(245, 247, 250, 0.6) 100%
  );
  z-index: 1;
`;

const HeroSection = styled.section`
  color: var(--text-primary);
  padding: 80px 0 60px;
  min-height: 600px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/images/hero/ai-office-transformation.webp') center/cover no-repeat;
    opacity: 0.05;
    z-index: 0;
  }

  .container {
    position: relative;
    z-index: 2;
  }

  @media (min-width: 768px) {
    padding: 100px 0 80px;
    min-height: 700px;
  }
`;

const HeroWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-3xl);
  align-items: center;
  width: 100%;  /* Use full container width */
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1.1fr;  /* Give video slightly more space */
    gap: var(--spacing-3xl);
  }
  
  @media (min-width: 1400px) {
    grid-template-columns: 1fr 1.15fr;  /* Balanced proportions */
    gap: var(--spacing-4xl);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  
  @media (min-width: 1024px) {
    text-align: left;
  }
`;

const HeroVideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.25);
  background: #000;
  border: 3px solid rgba(11, 152, 206, 0.15);
  
  @media (max-width: 1023px) {
    order: -1; /* Show video first on mobile */
    margin-bottom: var(--spacing-xl);
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (min-width: 1024px) {
    /* No max-width restriction - let it fill its grid column */
  }
  
  @media (min-width: 1600px) {
    max-width: 800px; /* Only restrict on very large screens */
  }
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const HeroVisual = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 767px) {
    order: -1;
    height: 200px;
  }
  
  @media (min-width: 768px) {
    height: 100%;
    min-height: 400px;
  }
`;

const LogoGraphic = styled.div`
  width: 100%;
  max-width: 400px;
  height: 400px;
  background: url('/logos/davinci-logo-white.svg') no-repeat center center;
  background-size: contain;
  opacity: 0.8;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3));
  animation: ${float} 6s ease-in-out infinite;
  
  @media (max-width: 767px) {
    max-width: 180px;
    height: 180px;
  }
  
  @media (min-width: 768px) and (max-width: 1023px) {
    max-width: 280px;
    height: 280px;
  }
`;

const HeroTitle = styled.h1`
  color: var(--text-primary);
  margin-bottom: var(--spacing-lg);
  animation: ${fadeIn} 0.8s ease-out;
  font-size: clamp(2.2rem, 4.5vw, 3.5rem);
  line-height: 1.1;
  font-weight: var(--font-weight-extra-bold);
  letter-spacing: var(--ls-tighter);
  background: linear-gradient(135deg, var(--text-primary) 0%, var(--color-accent-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (min-width: 1400px) {
    font-size: clamp(2.8rem, 4.5vw, 3.8rem);
  }
`;

const HeroTagline = styled.p`
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-md);
  color: var(--color-accent-primary);
  animation: fadeIn 0.8s ease-out 0.2s both;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  opacity: 0.9;

  @media (min-width: 768px) {
    font-size: var(--fs-md);
  }
`;

const HeroDescription = styled.p`
  font-size: var(--fs-md);
  line-height: 1.65;
  margin-bottom: var(--spacing-2xl);
  animation: fadeIn 0.8s ease-out 0.4s both;
  color: var(--text-secondary);
  
  @media (min-width: 1024px) {
    font-size: var(--fs-lg);
  }
  
  @media (min-width: 1400px) {
    font-size: calc(var(--fs-lg) * 1.15);
  }
`;

const HeroButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  animation: fadeIn 0.8s ease-out 0.6s both;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: var(--spacing-lg);
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  background-color: var(--color-accent-primary);
  color: white !important;
  padding: var(--spacing-md) var(--spacing-2xl);
  border-radius: 8px;
  font-weight: var(--font-weight-bold);
  text-align: center;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);
  position: relative;
  overflow: hidden;
  text-decoration: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(11, 152, 206, 0.05) 0%,
      rgba(11, 152, 206, 0) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
    background-color: var(--color-accent-secondary);
    color: white !important;
    text-decoration: none;
    
    &::before {
      opacity: 1;
    }
  }

  &:focus, &:active, &:visited {
    color: white !important;
    outline: none;
    box-shadow: 0 0 0 3px rgba(11, 152, 206, 0.2), var(--shadow-sm);
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: var(--text-secondary) !important;
  padding: var(--spacing-md) var(--spacing-2xl);
  border: 2px solid var(--color-accent-primary);
  border-radius: 8px;
  font-weight: var(--font-weight-medium);
  text-align: center;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  position: relative;

  &:hover {
    background-color: var(--color-accent-primary);
    color: white !important;
    transform: translateY(-1px);
    text-decoration: none;
  }

  &:focus, &:active {
    outline: none;
    box-shadow: 0 0 0 3px rgba(11, 152, 206, 0.2), var(--shadow-sm);
  }
  
  &:visited {
    color: var(--text-secondary) !important;
  }
`;

const ServicesSection = styled.section`
  padding: 60px 0;
  background: var(--color-primary-light);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at top right, rgba(11, 152, 206, 0.03) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (min-width: 768px) {
    padding: 80px 0;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: var(--spacing-lg);
  color: var(--text-primary);
  position: relative;
  z-index: 2;
  font-size: var(--fs-3xl);
  font-weight: var(--font-weight-bold);
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: var(--fs-lg);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-4xl);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 2;
  line-height: var(--lh-relaxed);
`;

const ServicesGrid = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  gap: var(--spacing-md);
  padding: 0 var(--spacing-md);
  
  /* Mobile: Single Column Stack */
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
  
  /* Tablet: Bento Grid Start */
  @media (min-width: 768px) {
    grid-template-columns: repeat(6, 1fr);
    grid-auto-rows: minmax(150px, auto);
    padding: 0;
    
    /* 2x2 layout: each card spans 3 columns */
    > * { grid-column: span 3; grid-row: span 1; }
  }
  
  /* Desktop: Refined Bento Grid */
  @media (min-width: 1024px) {
    gap: var(--spacing-md);
    max-width: 1200px;
    margin: 0 auto;
    grid-template-columns: repeat(6, 1fr);
    grid-auto-rows: minmax(180px, auto);
    
    /* 2x2 layout: each card spans 3 columns */
    > * { grid-column: span 3; grid-row: span 1; }
  }
`;

const ServiceCard = styled(Link)<{ $delay?: number; $variant?: number; }>`
  background: ${props => {
    const images = [
      'linear-gradient(rgba(11, 152, 206, 0.85), rgba(20, 53, 98, 0.85)), url(/images/ai-services-bg.png)',
      'linear-gradient(rgba(49, 110, 168, 0.85), rgba(20, 53, 98, 0.85)), url(/images/it-projects-bg.png)',
      'linear-gradient(rgba(73, 80, 87, 0.85), rgba(33, 37, 41, 0.85)), url(/images/managed-it-bg.png)',
      'linear-gradient(rgba(11, 152, 206, 0.85), rgba(49, 110, 168, 0.85)), url(/images/support-bg.png)'
    ];
    return props.$variant !== undefined ? images[props.$variant] : images[0];
  }};
  background-size: cover;
  background-position: center;
  padding: var(--spacing-lg);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.$delay || 0}ms;
  opacity: 0;
  position: relative;
  overflow: hidden;
  border: none;
  min-height: 160px;
  color: white;

  /* Ensure equal height cards */
  height: 100%;

  @media (min-width: 768px) {
    min-height: 220px;
    padding: var(--spacing-xl);
  }

  @media (min-width: 1024px) {
    min-height: 240px;
    padding: var(--spacing-xl) var(--spacing-lg);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -100%;
    right: -100%;
    width: 300%;
    height: 300%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 40%);
    animation: float 20s ease-in-out infinite;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
    border-radius: 16px;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(11, 152, 206, 0.2);
    border-color: var(--color-accent-primary);
    
    &::before {
      opacity: 1;
    }
    
    .service-icon {
      transform: scale(1.1) rotate(3deg);
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(11, 152, 206, 0.2), var(--shadow-sm);
  }
`;

const ServiceIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;

  svg {
    width: 20px;
    height: 20px;
    color: white;
    transition: all 0.3s ease;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;

const ServiceBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: var(--white);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-bold);
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  backdrop-filter: blur(8px);
`;

const CardContentGrid = styled.div`
  display: grid;
  row-gap: 12px;
  align-items: start;

  @media (min-width: 1024px) {
    grid-template-columns: 1.3fr 0.9fr;
    column-gap: var(--spacing-lg);
    row-gap: 12px;
  }
`;

const PillList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
  overflow-x: auto;

  /* Make pills stay in one row on larger screens */
  @media (min-width: 1024px) {
    flex-wrap: nowrap;
  }

  /* Allow wrapping on smaller screens if needed */
  @media (max-width: 1023px) {
    flex-wrap: wrap;
  }
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: var(--white);
  font-size: 11px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ServiceTitle = styled.h3`
  color: white;
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: var(--font-weight-bold);
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  @media (min-width: 1024px) {
    font-size: 20px;
  }
`;

const ServiceDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  flex: 1;
  line-height: 1.5;
  margin-bottom: var(--spacing-sm);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const ServiceFeature = styled.li`
  font-size: 13px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: 4px 0;
  
  &::before {
    content: '✓';
    color: white;
    font-weight: bold;
    background: rgba(255, 255, 255, 0.2);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const LearnMore = styled.span`
  font-size: 13px;
  color: white;
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  width: 100%;
  
  &::after {
    content: '→';
    transition: transform 0.3s ease;
    font-size: var(--fs-lg);
  }
  
  ${ServiceCard}:hover & {
    &::after {
      transform: translateX(6px);
    }
  }
`;

const ProofRow = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
  flex-wrap: wrap;
`;

const ProofItem = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 70px;

  .value { font-size: 18px; font-weight: var(--font-weight-bold); color: #fff; text-shadow: 0 2px 4px rgba(0,0,0,0.2); }
  .label { font-size: 10px; color: rgba(255,255,255,0.85); }
`;

const GridColumnRight = styled.div`
  @media (min-width: 1024px) {
    border-left: 1px solid rgba(255,255,255,0.15);
    padding-left: var(--spacing-md);
  }
`;

const GridColumnLeft = styled.div`
  @media (min-width: 1024px) {
    padding-right: var(--spacing-md);
  }
`;

const CTASection = styled.section`
  padding: var(--spacing-section-mobile) 0;
  background-color: var(--color-primary-base);
  color: var(--text-primary);
  text-align: center;
  position: relative;
  
  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--border-light), transparent);
  }
`;

const CTATitle = styled.h2`
  color: var(--text-primary);
  margin-bottom: var(--spacing-xl);
`;

const CTADescription = styled.p`
  font-size: var(--fs-lg);
  line-height: var(--lh-relaxed);
  margin-bottom: var(--spacing-3xl);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  color: var(--text-secondary);
`;

const ClientSection = styled.section`
  padding: var(--spacing-lg) 0;
  background: var(--light-grey);
  text-align: center;
  overflow: hidden;
`;

const ClientTitle = styled.h2`
  color: var(--davinci-grey);
  font-size: var(--fs-lg);
  margin-bottom: var(--spacing-md);
  font-weight: var(--font-weight-semibold);
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: var(--spacing-sm) 0;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 80px;
    z-index: 2;
    pointer-events: none;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(90deg, var(--color-primary-light), transparent);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(270deg, var(--color-primary-light), transparent);
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  animation: scroll 70s linear infinite;
  width: fit-content;
  
  &:hover {
    animation-play-state: paused;
  }
  
  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const ClientLogo = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  min-width: 140px;
  padding: var(--spacing-sm);
  opacity: 0.8;
  transition: all var(--transition-base);
  cursor: pointer;
  filter: grayscale(100%);
  
  img {
    max-height: 40px;
    max-width: 120px;
    width: auto;
    height: auto;
    object-fit: contain;
  }
  
  /* Fallback text for customers without logos */
  span {
    font-size: var(--fs-md);
    font-weight: var(--font-weight-medium);
    color: var(--davinci-grey);
    white-space: nowrap;
    text-align: center;
  }

  &:hover {
    opacity: 1;
    filter: grayscale(0%);
    transform: translateY(-2px) scale(1.02);
  }
  
  @media (max-width: 768px) {
    height: 60px;
    min-width: 120px;
    padding: var(--spacing-sm);
    
    img {
      max-height: 40px;
      max-width: 100px;
    }
    
    span {
      font-size: var(--fs-sm);
    }
  }
`;

// PartnersSection moved to TechnologyPartners component

// Client carousel styles (different from partner carousel)
const ClientCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: var(--spacing-md) 0;
  margin-top: var(--spacing-xl);

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, var(--color-primary-light), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, var(--color-primary-light), transparent);
  }
`;

const ClientCarouselTrack = styled.div`
  display: flex;
  gap: var(--spacing-2xl);
  align-items: center;
  width: fit-content;
  animation: partners-scroll 120s linear infinite;

  &:hover { animation-play-state: paused; }

  @keyframes partners-scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

// Keeping this for potential future use
const PartnerCard = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 12px;
  min-height: 72px;
  padding: var(--spacing-md);
  box-shadow: none;
  transition: all var(--transition-base);
  text-decoration: none;
  color: inherit;
  filter: grayscale(100%);
  isolation: isolate;
  overflow: hidden;
  opacity: 0.85;

  img {
    height: 64px;
    width: auto;
    object-fit: contain;
    mix-blend-mode: multiply;
    background: transparent;
    max-width: 160px;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: none;
    border-color: transparent;
    filter: grayscale(0%);
    opacity: 1;
  }

  @media (min-width: 768px) {
    min-height: 84px;
    img { height: 72px; width: auto; max-width: 180px; }
  }
`;

const Home: React.FC = () => {
  const [metrics, setMetrics] = useState({
    response_time: 215,  // Will be replaced by cached values
    response_time_display: '<4 hours',
    resolution_rate: 22,
    resolution_rate_display: '>20%',
    customer_satisfaction: 4.8,
    active_tickets: 20,
    customers_served: 40,
    active_clients_display: '25+'  // Default value for active clients display
  });
  
  const [customers, setCustomers] = useState<{name: string, industry?: string, logo_url?: string, processed_logo_url?: string, id?: string}[]>([]);
  
  // Partner data moved to TechnologyPartners component

  useEffect(() => {
    // First, try to load from static build-time cache (fastest)
    fetch('/initial-metrics.json')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setMetrics(prevMetrics => ({
            ...prevMetrics,
            ...Object.fromEntries(
              Object.entries(data.data).filter(([_, v]) => v !== null)
            )
          }));
          console.log('Loaded build-time cached metrics for initial display');
        }
      })
      .catch(() => {
        // Fallback to API cached metrics if static file fails
        fetch(apiEndpoint('/api/metrics'))
          .then(res => res.json())
          .then(data => {
            if (data.success && data.data) {
              setMetrics(prevMetrics => ({
                ...prevMetrics,
                ...Object.fromEntries(
                  Object.entries(data.data).filter(([_, v]) => v !== null)
                )
              }));
              console.log('Loaded API cached metrics for initial display');
            }
          })
          .catch(err => console.error('Error fetching cached metrics:', err));
      });

    // Then fetch live metrics (may update if newer data is available)
    setTimeout(() => {
      fetch(apiEndpoint('/api/metrics'))
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data) {
            // Round customers_served down to nearest 5 for consistency
            const processedData = { ...data.data };
            if (processedData.customers_served) {
              processedData.customers_served = Math.floor(processedData.customers_served / 5) * 5;
            }
            
            setMetrics(prevMetrics => ({
              ...prevMetrics,
              ...Object.fromEntries(
                Object.entries(processedData).filter(([_, v]) => v !== null)
              )
            }));
            console.log('Updated with live metrics');
          }
        })
        .catch(err => console.error('Error fetching live metrics:', err));
    }, 500); // Small delay to let cached metrics display first
      
    // Fetch customers from API
    fetch(apiEndpoint('/api/customers'))
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data && data.data.length > 0) {
          setCustomers(data.data);
          
          // Use total_served for the metric (ALL customers we serve)
          // This includes blacklisted customers as we still serve them
          const totalServed = data.total_served || data.total || data.data.length;
          
          // Round down to nearest 5 for display
          const roundedCount = Math.floor(totalServed / 5) * 5;
          
          // Update metrics with actual customer count
          setMetrics(prevMetrics => ({
            ...prevMetrics,
            customers_served: roundedCount
          }));
        }
      })
      .catch(err => console.error('Error fetching customers:', err));
  }, []);

  return (
    <>
      <HeroSection>
        <VideoBackground autoPlay loop muted playsInline>
          <source src="/hero-bg-cropped.mp4" type="video/mp4" />
        </VideoBackground>
        <VideoOverlay />
        <div className="container">
          <HeroWrapper>
            <HeroContent>
              <HeroTagline>ALBERTA'S AI IMPLEMENTATION EXPERTS</HeroTagline>
              <HeroTitle>Your Team + Our AI = Unfair Advantage</HeroTitle>
              <HeroDescription>
                Researchers build AI in labs. Consultants sell PowerPoints. 
                We're implementers - we make AI work in production, with your actual data, solving your real problems.
              </HeroDescription>
              <HeroButtons>
                <ModernButton as={PrimaryButton} to="/assessment" variant="primary">
                  Get Your AI Assessment
                </ModernButton>
              </HeroButtons>
            </HeroContent>
            <HeroVideoWrapper>
              <iframe 
                src="https://www.youtube.com/embed/V5ZyxdUAimc?rel=0&modestbranding=1&autoplay=0&controls=1"
                title="Davinci AI Solutions Overview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </HeroVideoWrapper>
          </HeroWrapper>
        </div>
      </HeroSection>

      <ClientSection>
        <div className="container">
          <ClientTitle>Trusted by Alberta Businesses</ClientTitle>
        </div>
        <ClientCarouselContainer>
          <ClientCarouselTrack>
            {/* Duplicate the list for seamless infinite scroll */}
            {[...customers, ...customers].map((customer, index) => (
              <ClientLogo key={index} title={customer.name}>
                {customer.processed_logo_url || customer.logo_url ? (
                  <img 
                    src={
                      customer.processed_logo_url
                        ? `${apiEndpoint('')}${customer.processed_logo_url}`
                        : customer.logo_url && customer.logo_url.startsWith('http')
                          ? customer.logo_url
                          : `${apiEndpoint('')}${customer.logo_url}`
                    } 
                    alt={customer.name}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      const img = e.currentTarget;
                      // If processed logo fails, try original logo
                      if (customer.processed_logo_url && customer.logo_url) {
                        img.src = customer.logo_url.startsWith('http') 
                          ? customer.logo_url 
                          : `${apiEndpoint('')}${customer.logo_url}`;
                        // Remove processed_logo_url to prevent infinite loop
                        delete customer.processed_logo_url;
                      } else {
                        // If all fails, show name
                        img.style.display = 'none';
                        const span = img.nextSibling as HTMLElement;
                        if (span) span.style.display = 'inline';
                      }
                    }}
                  />
                ) : null}
                <span style={{ display: customer.logo_url ? 'none' : 'inline' }}>
                  {customer.name}
                </span>
              </ClientLogo>
            ))}
          </ClientCarouselTrack>
        </ClientCarouselContainer>
      </ClientSection>

      <ServicesSection>
        <div className="container">
          <SimpleFadeIn delay={100}>
            <SectionTitle>Our AI Solutions</SectionTitle>
          </SimpleFadeIn>
          <SimpleFadeIn delay={200}>
            <SectionSubtitle>
              Four ways we turn teams into workplace superheroes with proven AI implementations.
            </SectionSubtitle>
          </SimpleFadeIn>
          <ServicesGrid>
            <SimpleFadeIn delay={300}>
              <ServiceCard 
                to="/services/ai-solutions" 
                $delay={100} 
                $variant={0}
              >
                <ServiceBadge>Most Popular</ServiceBadge>
                <ServiceIcon className="service-icon">
                  <CpuChipIcon />
                </ServiceIcon>
                <ServiceTitle>AI Solutions</ServiceTitle>
                <ServiceDescription>
                  Turn repetitive tasks into superpowers. We implement AI that augments your team's capabilities,
                  helping them chase 10x faster workflows.
                </ServiceDescription>
                <ServiceFeatures>
                  <ServiceFeature>Chase 10x faster workflows with AI automation</ServiceFeature>
                  <ServiceFeature>Deploy AI copilots that make staff 10x more capable</ServiceFeature>
                  <ServiceFeature>Augment decision-making with intelligent insights</ServiceFeature>
                </ServiceFeatures>
                <ProofRow>
                  <ProofItem>
                    <div className="value">650%</div>
                    <div className="label">Efficiency Gain</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">24/7</div>
                    <div className="label">Always On</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">100%</div>
                    <div className="label">Custom Built</div>
                  </ProofItem>
                </ProofRow>
                <LearnMore>See Superhero Transformations</LearnMore>
              </ServiceCard>
            </SimpleFadeIn>
            <SimpleFadeIn delay={400}>
              <ServiceCard 
                to="/services/it-projects" 
                $delay={200} 
                $variant={1}
              >
                <ServiceBadge>Fixed Price</ServiceBadge>
                <ServiceIcon className="service-icon">
                  <ComputerDesktopIcon />
                </ServiceIcon>
                <ServiceTitle>IT Infrastructure Projects</ServiceTitle>
                <ServiceDescription>
                  Storage upgrades, network redeployments, server migrations—delivered on time and on budget.
                </ServiceDescription>
                <ServiceFeatures>
                  <ServiceFeature>Server upgrades and migrations with zero downtime</ServiceFeature>
                  <ServiceFeature>Network infrastructure redesigns and implementations</ServiceFeature>
                  <ServiceFeature>Fixed pricing with guaranteed delivery timelines</ServiceFeature>
                </ServiceFeatures>
                <ProofRow>
                  <ProofItem>
                    <div className="value">&lt;90d</div>
                    <div className="label">Lead Time</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">30d</div>
                    <div className="label">Post-launch warranty</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">Fixed</div>
                    <div className="label">Pricing</div>
                  </ProofItem>
                </ProofRow>
                <LearnMore>View Project Portfolio</LearnMore>
              </ServiceCard>
            </SimpleFadeIn>
            <SimpleFadeIn delay={500}>
              <ServiceCard 
                to="/services/managed-it" 
                $delay={300} 
                $variant={2}
              >
                <ServiceBadge>AI-Powered</ServiceBadge>
                <ServiceIcon className="service-icon">
                  <ShieldCheckIcon />
                </ServiceIcon>
                <ServiceTitle>Superpowered IT Services</ServiceTitle>
                <ServiceDescription>
                  AI-augmented monitoring and management that makes your IT team superhuman.
                </ServiceDescription>
                <ServiceFeatures>
                  <ServiceFeature>AI predicts and prevents issues before they impact workflows</ServiceFeature>
                  <ServiceFeature>Automated reporting that writes itself</ServiceFeature>
                  <ServiceFeature>We use the same AI tools to manage our own infrastructure</ServiceFeature>
                </ServiceFeatures>
                <ProofRow>
                  <ProofItem>
                    <div className="value">24/7</div>
                    <div className="label">Monitoring</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">{metrics?.active_clients_display || '25+'}</div>
                    <div className="label">Active Clients</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">365</div>
                    <div className="label">Days/Year</div>
                  </ProofItem>
                </ProofRow>
                <LearnMore>See AI-Powered IT in Action</LearnMore>
              </ServiceCard>
            </SimpleFadeIn>
            <SimpleFadeIn delay={600}>
              <ServiceCard 
                to="/services/helpdesk" 
                $delay={400} 
                $variant={3}
              >
                <ServiceBadge>Superhero Support</ServiceBadge>
                <ServiceIcon className="service-icon">
                  <MegaphoneIcon />
                </ServiceIcon>
                <ServiceTitle>AI-Powered Helpdesk Support</ServiceTitle>
                <ServiceDescription>
                  Our support team uses AI superpowers to resolve issues faster and smarter than humanly possible.
                </ServiceDescription>
                <ServiceFeatures>
                  <ServiceFeature>AI predicts and resolves issues before they impact you</ServiceFeature>
                  <ServiceFeature>Instant solutions across all channels with superhuman accuracy</ServiceFeature>
                  <ServiceFeature>Our team uses the same AI tools we implement for you</ServiceFeature>
                </ServiceFeatures>
                <ProofRow>
                  <ProofItem>
                    <div className="value">{metrics?.active_clients_display || '25+'}</div>
                    <div className="label">Active Clients</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">{metrics?.response_time_display || '<8h'}</div>
                    <div className="label">Response Time</div>
                  </ProofItem>
                  <ProofItem>
                    <div className="value">{metrics?.resolution_rate_display || '>40%'}</div>
                    <div className="label">First-Call Fix</div>
                  </ProofItem>
                </ProofRow>
                <LearnMore>Experience Superhero Support</LearnMore>
              </ServiceCard>
            </SimpleFadeIn>
          </ServicesGrid>
        </div>
      </ServicesSection>

      {/* Technology Partners Section - using shared component */}
      <TechnologyPartners />
      
      {/* Use cases - will add back with real content */}
      {/* <UseCases /> */}

      {/* Testimonials - will add back with real testimonials */}
      {/* <TestimonialsSection /> */}

      {/* Case studies - hidden until content is ready */}
      {/* <CaseStudiesSection /> */}
      
      <FAQ />

      <CTASection>
        <div className="container">
          <SimpleFadeIn delay={100}>
            <CTATitle>Join the 25% of Alberta Businesses Already Winning with AI</CTATitle>
          </SimpleFadeIn>
          <SimpleFadeIn delay={200}>
            <CTADescription>
              AI adoption isn't coming - it's here. We'll make sure you lead, not follow. 
              Every solution we build is battle-tested on our own operations first.
            </CTADescription>
          </SimpleFadeIn>
          <SimpleFadeIn delay={400}>
            <ModernButton as={PrimaryButton} to="/assessment" variant="primary">
              Get Your AI Assessment
            </ModernButton>
          </SimpleFadeIn>
        </div>
      </CTASection>
    </>
  );
};

export default Home;
